import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Dialog,
    ListItem, Button, Badge, List, Box
} from "@mui/material";
import {useCargoList, useCargoListOptions} from "~/apps/packages/auto-visits/hooks";
import {makeStyles, useTheme} from "@mui/styles";
import {SearchAppBar} from "~/apps/core/ui/SearchAppBar";
import {CargoV2} from "~/apps/packages/auto-visits/queries";
import {PortTheme} from "~/ui/theme";
import {useHistoryState} from "~/apps/core/utils";
import {ArrayElement, tuple} from "~/types";
import {noun as plural} from "plural-ru";
import {FloatingActions} from "~/apps/core/ui/FloatingActions";
import {LoadingResult} from "~/apps/core/ui/LoadingResult";
import {useBottomScrollListener} from "~/lib/useBottomScrollListener";
import {CargoTypeChipInput} from "~/apps/packages/auto-visits/components/AutoVisitForm/CargoTypeChipInput";
import {CargoDirectionChipInput} from "~/apps/packages/auto-visits/components/AutoVisitForm/CargoDirectionChipInput";
import {FiltersBar} from "~/apps/core/ui/FiltersBar";
import {CargoSelectDialogItem} from "~/apps/packages/auto-visits/components/AutoVisitForm/CargoSelectDialogItem";
import {AbsoluteContainer} from "~/apps/core/ui/AbsoluteContainer";
import useMergedRef from "@react-hook/merged-ref";
import {useChangeEffect} from "~/lib/useChangeEffect";
import {LoadingBox} from "~/apps/core/ui/LoadingBox";

type Value = CargoV2[];


export type CargoSelectDialogProps = {
    open?: boolean
    onClose?: () => void
    autoVisitId?: string
    autoVisitDate?: Date
    value: Value
    onChange?: (value: Value) => void
}


const useStyles = makeStyles<PortTheme, CargoSelectDialogProps>(theme => ({
    paper: {
        backgroundColor: theme.palette.background.default
    },
    search: {
        "& input": {
            color: "white"
        }
    },
    item: {
        width: "100%"
    },
    badge: {
        "& .MuiBadge-badge": {
            position: "inherit !important",
            transform: "none",
        },
    },
}));


type FilterState = {
    query?: string
    direction?: CargoV2["direction"]
    type?: useCargoListOptions["type"]
}


function useFilterState() {
    const [value, setValue, delValue] = useHistoryState<FilterState>({
        name: "f",
        handleBack: false,
        serialize: value => JSON.stringify(value),
        deserialize: data => {
            const raw = JSON.parse(data);
            const value: FilterState = {};
            if (typeof raw == "object") {
                if (typeof raw.query == "string" && raw.query.length > 0) {
                    value.query = raw.query;
                }
                if (typeof raw.direction == "string" && raw.direction.length > 0) {
                    value.direction = raw.direction;
                }
                if (Array.isArray(raw?.type) && raw.type.length > 0) {
                    value.type = raw.type;
                }
            }
            return Object.keys(value).length ? value : null;
        },
    });

    const setFilter = useCallback(async value => {
        if (value?.query?.length > 0 || value?.direction?.length > 0 || value?.type?.length > 0) {
            setValue(value);
        } else {
            await delValue();
        }
    }, [setValue, delValue]);

    return tuple(value, setFilter);
}


export const CargoSelectDialog = (props: CargoSelectDialogProps) => {

    const {open, onClose, autoVisitId, autoVisitDate, value, onChange} = props;
    const theme = useTheme<PortTheme>();
    const classes = useStyles(props);
    const [filter, setFilter] = useFilterState();
    const [selectedCargos, setSelectedCargos] = useState<Value>([]);
    const selectedCaroIdSet = useMemo(() => new Set(selectedCargos.map(x => x.code)), [selectedCargos]);
    // обновляем исключающие грузы только при открытии/закрытии окна
    const excludeCargos = useMemo(() => {
        return value;
    }, [open]);
    const {data, fetchMore, loading, canFetchMore} = useCargoList({
        autoVisitId,
        date: autoVisitDate,
        type: filter?.type,
        query: filter?.query,
        direction: filter?.direction ? [filter.direction] : null,
        excludeCargoIds: Array.from(excludeCargos?.map(x => x.code))
    });

    const $container = useRef<HTMLDivElement>();
    const $scrollableContainer = useBottomScrollListener(() => {
        if (open) {
            fetchMore();
        }
    }, 150, 100, false);
    const $mergedRef = useMergedRef($container, $scrollableContainer);

    // reset filter when close
    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                setSelectedCargos([]);
                setFilter({}).then(() => null);
            }, theme.transitions.duration.leavingScreen);
        }
    }, [open]);

    useChangeEffect(() => {
        $container.current?.scrollTo(0, 0);
    }, [filter])

    const handleSearch = (query) => {
        setFilter({
            ...filter,
            query: query,
        }).then(() => null);
    }

    const handleListItemClick = (item: ArrayElement<Value>) => () => {
        setSelectedCargos(cargos => {
            if (cargos.find(c => c.code == item.code)) {
                cargos = cargos.filter(c => c.code != item.code);
            } else {
                cargos = [...cargos, item];
            }
            return cargos;
        });
    }

    const handleApplyClick = () => {
        onChange && onChange([...value, ...selectedCargos]);
    }

    const handleClose = () => {
        onClose && onClose();
    }

    const handleDirectionChange = (direction) => {
        setFilter({
            ...filter,
            direction: direction
        }).then(() => null);
    }

    const handleCargoTypeChange = (type) => {
        setFilter({
            ...filter,
            type: type
        }).then(() => null);
    }

    return (
        <Dialog open={open} fullScreen={true} onClose={handleClose}
                PaperProps={{className: classes.paper}}>

            <SearchAppBar
                title={filter?.query || "Выберите грузы"}
                position="fixed"
                color="default"
                query={filter?.query}
                onSearch={handleSearch}
            >
                <FiltersBar>
                    <CargoTypeChipInput value={filter?.type} onChange={handleCargoTypeChange}/>
                    <CargoDirectionChipInput value={filter?.direction} onChange={handleDirectionChange}/>
                </FiltersBar>
            </SearchAppBar>
            <AbsoluteContainer disableGutters top={104} ref={$mergedRef}>
                <LoadingBox loading={loading} empty={data?.length == 0} placeholder={canFetchMore}>
                    {data?.length > 0 && (
                        <List>
                            {data.map(item => {
                                const selected = selectedCaroIdSet.has(item.code);
                                return (
                                    <ListItem
                                        key={item.code}
                                    >
                                        <CargoSelectDialogItem cargo={item} selected={selected} onClick={handleListItemClick(item)}/>
                                    </ListItem>
                                )
                            })}
                        </List>
                    )}
                </LoadingBox>

                {selectedCargos?.length > 0 && (
                    <>
                        <Box minHeight="80px"/>
                        <FloatingActions>
                            <Button
                                color="additionalPrimary"
                                fullWidth
                                endIcon={(
                                    <Badge
                                        badgeContent={selectedCargos.length}
                                        showZero
                                        className={classes.badge}
                                        color="onAdditionalPrimary"
                                    />
                                )}
                                onClick={handleApplyClick}
                                disabled={loading}
                            >
                                {plural(selectedCargos.length, "Добавить груз", "Добавить грузы", "Добавить грузы")}
                            </Button>
                        </FloatingActions>
                    </>
                )}
            </AbsoluteContainer>
        </Dialog>
    );
}